import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import { DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ScrollArea from "../ui/shared/ScrollArea";
import { useContacts } from "@/hooks/useContacts";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useCustomFields } from "@/hooks/useCustomFields";
import { useAuth } from "@/contexts/AuthContext";
import { clampAttributionIdForSave } from "@/lib/chatContactScope";
import { useTranslation } from "react-i18next";
import {
  COUNTRY_CALLING_CODES,
  DEFAULT_COUNTRY_CALLING_CODE,
  getCountryCallingCodeLabel,
} from "@/data/countryCallingCodes";

const AddNewContact: React.FC = () => {
  const { t } = useTranslation("crm");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { createContact } = useContacts();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const { fields: customFields } = useCustomFields();
  const { user } = useAuth();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  const canAssignToOthers = user?.can_assign_contacts === true || user?.type === 'agency_user';
  const defaultAttributionId = canAssignToOthers ? '' : (user?.auth_user_id || '');

  const [formData, setFormData] = useState({
    display_name: '',
    email: '',
    phone: '',
    countryCode: DEFAULT_COUNTRY_CALLING_CODE,
    jid: '',
    is_group: false,
    account_assistant_id: '',
    assistant_is_active: false,
    attribution_id: defaultAttributionId,
  });

  const [attributionSearch, setAttributionSearch] = useState('');

  const handleFormChange = (field: string, value: any) => {
    // Se for o campo de telefone, aceitar apenas números
    if (field === 'phone') {
      const onlyNumbers = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [field]: onlyNumbers }));
      return;
    }

    // Se for o campo de assistente, ativar automaticamente quando selecionado
    if (field === 'account_assistant_id') {
      const isAssistantSelected = value && value !== 'none' && value !== '';
      setFormData(prev => ({
        ...prev,
        [field]: value,
        assistant_is_active: isAssistantSelected
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      display_name: '',
      email: '',
      phone: '',
      countryCode: DEFAULT_COUNTRY_CALLING_CODE,
      jid: '',
      is_group: false,
      account_assistant_id: '',
      assistant_is_active: false,
      attribution_id: defaultAttributionId,
    });
    setMessage(null);
    setCustomFieldValues({});
  };

  const generateJidFromPhone = (phone: string, countryCode: string): string => {
    // Remove todos os caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');

    // Se o número já começa com o código do país, usar como está
    if (cleanPhone.startsWith(countryCode)) {
      return `${cleanPhone}@s.whatsapp.net`;
    }

    // Caso contrário, adicionar o código do país no início
    const formattedPhone = `${countryCode}${cleanPhone}`;
    return `${formattedPhone}@s.whatsapp.net`;
  };

  const handleSave = async () => {
    if (!formData.display_name.trim()) {
      setMessage({ type: 'error', text: 'Nome do contato é obrigatório' });
      return;
    }

    if (!formData.phone.trim()) {
      setMessage({ type: 'error', text: 'Telefone é obrigatório' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Gerar JID automaticamente baseado no telefone e código do país
      const jid = generateJidFromPhone(formData.phone, formData.countryCode);

      // Formatar telefone com código do país para armazenamento
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const fullPhone = cleanPhone.startsWith(formData.countryCode)
        ? cleanPhone
        : `${formData.countryCode}${cleanPhone}`;

      const { phone: _, countryCode: __, ...restFormData } = formData;

      const hasCustomFields = Object.keys(customFieldValues).length > 0;
      const rawAttribution = formData.attribution_id === 'none' || formData.attribution_id === ''
        ? null
        : formData.attribution_id;
      const safeAttributionId = clampAttributionIdForSave(rawAttribution, user ?? null);
      const contactData = {
        ...restFormData,
        jid,
        phone: fullPhone,
        name: formData.display_name,
        last_message_id: null,
        last_message_at: null,
        profile_picture: null,
        profile_picture_updated_at: null,
        account_assistant_id: formData.account_assistant_id === 'none' || formData.account_assistant_id === '' ? null : formData.account_assistant_id,
        attribution_id: safeAttributionId,
        push_name_last: formData.display_name,
        countryCallingCode: formData.countryCode,
        ...(hasCustomFields ? { custom_fields: customFieldValues } : {})
      };

      const result = await createContact(contactData);

      if (result.success) {
        setMessage({ type: 'success', text: 'Contato criado com sucesso!' });
        resetForm();
        setTimeout(() => {
          setOpen(false);
        }, 1500);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Não foi possível criar o contato.',
        });
      }
    } catch (error) {
      console.error('Erro ao criar contato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger className="h-12 px-4 rounded-[12px] bg-primary hover:bg-primary-hover text-primary-foreground font-medium text-[14px] flex justify-center items-center gap-2 shadow-sm transition-colors duration-200">
        <Plus size={18} className="text-primary-foreground" />
        <p>{t("newContact.button")}</p>
      </DialogTrigger>
      <DialogContent className="flex w-full p-0 border-border bg-card flex-col">
        <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
          <div className="w-full flex flex-col gap-4">
            {message && (
              <div className={`p-3 rounded-md ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-500/20 dark:border-green-500/30 dark:text-green-300' 
                  : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-500/20 dark:border-red-500/30 dark:text-red-300'
              }`}>
                {message.text}
              </div>
            )}

            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-foreground">{t("newContact.title")}</DialogTitle>
            </DialogHeader>

            <form className="w-full flex flex-col gap-4">
              {/* Nome */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="name">{t("common.nameRequired")}</Label>
                <Input
                  placeholder={t("common.contactNamePlaceholder")}
                  id="name"
                  value={formData.display_name}
                  onChange={(e) => handleFormChange('display_name', e.target.value)}
                  className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                  type="text"
                  required
                />
              </div>

              {/* Email */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="email">{t("common.email")}</Label>
                <Input
                  placeholder="email@exemplo.com"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                  type="email"
                />
              </div>

              {/* Telefone com código do país */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="phone">{t("common.phoneRequired")}</Label>
                <div className="flex gap-2 w-full">
                  <Select value={formData.countryCode} onValueChange={(value) => handleFormChange('countryCode', value)}>
                    <SelectTrigger className="bg-input text-[14px] w-[168px] shrink-0 placeholder:text-muted-foreground text-foreground !h-[36px] border focus:ring-primary/30 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      <SelectGroup>
                        {COUNTRY_CALLING_CODES.map((country) => (
                          <SelectItem key={`${country.code}-${country.name}`} value={country.code}>
                            <span>{country.flag} {getCountryCallingCodeLabel(country)}</span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="11999999999"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    className="bg-input flex-1 text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("newContact.phoneHint")}
                </p>
              </div>

              {/* Assistente */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="assistant">{t("common.assistant")}</Label>
                <Select value={formData.account_assistant_id} onValueChange={(value) => handleFormChange('account_assistant_id', value)}>
                  <SelectTrigger className="bg-input text-[14px] w-full placeholder:text-muted-foreground text-foreground !h-[40px] border focus:ring-primary/30 border-border">
                    <SelectValue placeholder={t("common.pickAssistant")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">{t("common.noAssistant")}</SelectItem>
                      {assistants.filter(a => a.is_active).map((assistant) => (
                        <SelectItem key={assistant.id} value={assistant.id}>
                          {assistant.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {/* Responsável pelo Contato */}
              <div className="flex flex-col w-full items-start gap-3">
                <Label htmlFor="attribution">{t("common.assignTo")}</Label>
                <Select
                  value={formData.attribution_id}
                  onValueChange={(value) => handleFormChange('attribution_id', value)}
                  onOpenChange={(open) => {
                    if (!open) setAttributionSearch('');
                  }}
                  disabled={!canAssignToOthers}
                >
                  <SelectTrigger className="bg-input text-[14px] w-full placeholder:text-muted-foreground text-foreground !h-[40px] border focus:ring-primary/30 border-border">
                    <SelectValue placeholder={t("common.pickOwner")} />
                  </SelectTrigger>
                  <SelectContent>
                    {canAssignToOthers && (
                      <div className="px-2 pb-2">
                        <Input
                          placeholder={t("common.searchOwner")}
                          value={attributionSearch}
                          onChange={(e) => setAttributionSearch(e.target.value)}
                          className="h-8 bg-input border-border text-foreground"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    <SelectGroup>
                      {canAssignToOthers && (
                        <SelectItem value="none">{t("common.noOwner")}</SelectItem>
                      )}
                      {members
                        .filter(m => m.is_active && m.auth_user_id)
                        .filter(m =>
                          canAssignToOthers ? true : m.auth_user_id === user?.auth_user_id
                        )
                        .filter(m => {
                          if (!attributionSearch) return true;
                          const searchLower = attributionSearch.toLowerCase();
                          const name = (m.name || '').toLowerCase();
                          const email = (m.email || '').toLowerCase();
                          return name.includes(searchLower) || email.includes(searchLower);
                        })
                        .map((member) => (
                          <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                            {member.name || member.email}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {!canAssignToOthers && (
                  <p className="text-xs text-muted-foreground">
                    {t("common.onlySelf")}
                  </p>
                )}
              </div>

              {/* Campos Personalizados */}
              {customFields.filter(f => f.is_active).length > 0 && (
                <div className="flex flex-col w-full items-start gap-3 border-t border-border pt-4">
                  <p className="text-sm font-semibold text-foreground">{t("newContact.customFields")}</p>
                  {customFields.filter(f => f.is_active).map((field) => (
                    <div key={field.id} className="flex flex-col w-full items-start gap-2">
                      <Label htmlFor={`cf-${field.id}`}>
                        {field.name}{field.is_required ? ' *' : ''}
                      </Label>
                      {field.field_type === 'text' && (
                        <Input
                          id={`cf-${field.id}`}
                          placeholder={field.name}
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                        />
                      )}
                      {field.field_type === 'number' && (
                        <Input
                          id={`cf-${field.id}`}
                          type="number"
                          placeholder={field.name}
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                        />
                      )}
                      {field.field_type === 'date' && (
                        <Input
                          id={`cf-${field.id}`}
                          type="date"
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className="bg-input text-[14px] placeholder:text-muted-foreground text-foreground min-h-[36px] border focus:ring-primary/30 border-border"
                        />
                      )}
                      {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                        <Select
                          value={customFieldValues[field.name] || ''}
                          onValueChange={(value) => setCustomFieldValues(prev => ({ ...prev, [field.name]: value }))}
                        >
                          <SelectTrigger className="bg-input text-[14px] w-full placeholder:text-muted-foreground text-foreground !h-[40px] border focus:ring-primary/30 border-border">
                            <SelectValue placeholder={`Selecione ${field.name}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="__none__">{t("common.none")}</SelectItem>
                              {(field.options || []).map((option: string) => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="w-full flex justify-end gap-3 mt-4">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="min-w-[120px]"
                >
                  {t("common.cancel")}
                </Button>
                <Button 
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="min-w-[120px]"
                >
                  {loading ? t('newContact.saving') : t('newContact.save')}
                </Button>
              </div>
            </form>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewContact;